/**
 * The print template model as pure rules (piece 27): the three families, the
 * regions each numbers, the geometry a region ships at, the column set Region 8
 * draws through, and the windowed-envelope band Regions 2 and 5 sit inside.
 *
 * 🔒 One region model serves every document. A family differs in which regions
 * it numbers above ten and in Region 8's column set, never in how a region is
 * positioned, so the editor, the preview and the sheet stay one mechanism.
 */
import { PRINT_REGIONS, printColumnHeading, printDrawnColumns, printFamilyOf, type PrintFamily, type PrintLineColumn, type PrintRegionValue } from '../documents/printRegions';
import { t, type StringKey } from '../strings';
import { printMarginProblem } from './settings';
import type { PrintTemplateContentRow, PrintTemplateStyle } from './content';

/**
 * A region belongs to the page, to a check's own band, or to one of the two
 * voucher stubs repeating it. 🔒 Laser voucher check stock is perforated at
 * three and a half and at seven inches, so the sheet is a check and two stubs
 * and each stub is a band of its own (decision 0558).
 */
export const PRINT_BAND_ROLES = ['Page', 'CheckBand', 'StubBand', 'SecondStubBand'] as const;
export type PrintBandRole = (typeof PRINT_BAND_ROLES)[number];

/** US Letter portrait, the size every template is seeded on (piece 27). */
export const LETTER_PAGE = { widthInches: 8.5, heightInches: 11 } as const;

/** A4 portrait, in inches to two decimals, which is how every other measurement on a sheet is carried. */
export const A4_PAGE = { widthInches: 8.27, heightInches: 11.69 } as const;

/**
 * 🔒 The two sizes a template may be laid out on, both portrait (decision
 * 0577). 💰 A5 is not among them: the seeded line table is seven and a half
 * inches wide and A5 is under six, so every template would open refused.
 */
export const PRINT_PAGE_SIZES = ['Letter', 'A4'] as const;
export type PrintPageSize = (typeof PRINT_PAGE_SIZES)[number];

/** What each named size measures. */
export const PRINT_PAGE_INCHES: Readonly<Record<PrintPageSize, { widthInches: number; heightInches: number }>> = {
  Letter: LETTER_PAGE,
  A4: A4_PAGE,
};

/**
 * The named size a stored page is, or null where it is neither. 🔨 Stored
 * inches are the record and the name is read off them, so a template written
 * before this choice existed needs no migration to answer Letter.
 */
export function printPageSizeOf(page: { widthInches: number; heightInches: number }): PrintPageSize | null {
  return PRINT_PAGE_SIZES.find((size) => Math.abs(PRINT_PAGE_INCHES[size].widthInches - page.widthInches) < 0.01 && Math.abs(PRINT_PAGE_INCHES[size].heightInches - page.heightInches) < 0.01) ?? null;
}

/**
 * 🔒 The top third of the page, which is where a sheet folded in thirds creases
 * (decision 0557). The specification puts both mailing addresses inside the top
 * third, because that is the band a window envelope can expose, and the fold is
 * the floor of it rather than a number somebody rounded up. It is derived from
 * the page height so the two can never come to disagree.
 */
export const LETTER_TOP_THIRD_INCHES = LETTER_PAGE.heightInches / 3;

/**
 * 🔒 Where a sheet folded in thirds creases, on whatever size it is printed
 * (decision 0577). A4 is taller than Letter, so its fold is lower; reading the
 * fold off a constant put the envelope band of an A4 sheet two-fifths of an inch
 * above its own crease.
 */
export function printTopThirdInches(page: { widthInches: number; heightInches: number } = LETTER_PAGE): number {
  return page.heightInches / 3;
}

/**
 * 🔒 Where a sheet folded in thirds creases, both folds, in inches down that
 * sheet (T190; decision 0661): a third and two thirds of its own height, so an A4
 * template's marks stand at A4's folds and never at Letter's.
 */
export function printFoldLinesInches(page: { heightInches: number } = LETTER_PAGE): readonly [number, number] {
  return [page.heightInches / 3, (page.heightInches * 2) / 3];
}

/**
 * 🔒 A fold mark's length across the page: a tenth of an inch (T206; decision
 * 0695, amending 0661's 0.2). The owner, 2026-09-24: *"Shorten the fold lines to
 * be a small dash but big enough for the printer to print."* 🔨 Not shorter: a
 * dash stands inside the printable margin a quarter inch in, and a tenth of an
 * inch at three-quarters of a point still prints as a line rather than a speck.
 */
export const PRINT_FOLD_MARK_LENGTH_INCHES = 0.1;

/**
 * 🔒 How far a fold mark stands in from the page's own edge (decision 0661).
 * 🔨 Not at the edge itself: an office laser printer leaves about a sixth of an
 * inch at each edge unprinted, so a mark drawn from the edge in prints as a
 * sliver or not at all. A quarter of an inch clears that and stays in the
 * half-inch margin, outside every seeded region.
 */
export const PRINT_FOLD_MARK_INSET_INCHES = 0.25;

/** A measurement as the refusals print it: two decimals, so 3.67 rather than the whole division. */
function inchesLabel(value: number): string {
  return String(Math.round(value * 100) / 100);
}

/**
 * A band is the area a region's X and Y are measured inside. The page is one
 * band for every accounting document; a check sheet is a check band with a
 * voucher stub beneath it, each three and a half inches deep.
 *
 * 🔒 Measuring inside the band is what keeps the envelope constraint one rule
 * across the three families: the stub repeats the payee at the same place in
 * its own band that the check band shows it in, and both are checked the same
 * way (decision 0517).
 */
export const CHECK_BAND_HEIGHT_INCHES = 3.5;

/**
 * 🔒 The perforations on laser voucher check stock, in inches down a US Letter
 * sheet (decision 0558): the check tears off at three and a half, the first
 * voucher stub at seven, and the second runs to the foot of the page. The band
 * origins are those perforations and nothing else.
 */
export const PRINT_BAND_ORIGIN_INCHES: Readonly<Record<PrintBandRole, number>> = {
  Page: 0,
  CheckBand: 0,
  StubBand: CHECK_BAND_HEIGHT_INCHES,
  SecondStubBand: CHECK_BAND_HEIGHT_INCHES * 2,
};

/** The area a band's regions are positioned inside. */
export function printBandArea(bandRole: PrintBandRole, page: { widthInches: number; heightInches: number } = LETTER_PAGE): { widthInches: number; heightInches: number } {
  return bandRole === 'Page' ? page : { widthInches: page.widthInches, heightInches: CHECK_BAND_HEIGHT_INCHES };
}

/** The named column sets a `PrintTemplateRegion` may hold; empty means the family's own default. */
export const PRINT_COLUMN_SETS = ['Transactional', 'TransactionalWithImage', 'TransactionalWithReference', 'Packing', 'Field', 'Receipt', 'Stub', 'Statement', 'Request', 'Materials', 'Service'] as const;
export type PrintColumnSet = (typeof PRINT_COLUMN_SETS)[number];

/** 🔒 The name the template editor shows for each column set, through the one lookup rather than the stored identifier (review, 2026-09-23). */
export const PRINT_COLUMN_SET_LABEL_KEYS: Readonly<Record<PrintColumnSet, StringKey>> = {
  Transactional: 'print.columnSet.transactional',
  TransactionalWithImage: 'print.columnSet.transactionalWithImage',
  TransactionalWithReference: 'print.columnSet.transactionalWithReference',
  Packing: 'print.columnSet.packing',
  Field: 'print.columnSet.field',
  Receipt: 'print.columnSet.receipt',
  Stub: 'print.columnSet.stub',
  Statement: 'print.columnSet.statement',
  Request: 'print.columnSet.request',
  Materials: 'print.columnSet.materials',
  Service: 'print.columnSet.service',
};

/** A column set's name as a person reads it; a value outside the list answers itself, so a stray row reads as a gap. */
export function printColumnSetLabel(set: string): string {
  return (PRINT_COLUMN_SETS as readonly string[]).includes(set) ? t(PRINT_COLUMN_SET_LABEL_KEYS[set as PrintColumnSet]) : set;
}

/**
 * 🔨 The Image column is off by default and never draws over three inches a
 * side (decision 0269), so a catalogue photo cannot push the totals off the
 * page. The stored size is the Item's; this is the ceiling it is clamped to.
 */
export const PRINT_IMAGE_MAX_INCHES = 3;

export function clampPrintImageInches(storedInches: number): number {
  if (!Number.isFinite(storedInches) || storedInches <= 0) return 0;
  return Math.min(storedInches, PRINT_IMAGE_MAX_INCHES);
}

const TRANSACTIONAL_COLUMNS: readonly PrintLineColumn[] = ['lineNumber', 'item', 'description', 'mfrPart', 'qty', 'unitPrice', 'discount', 'amount'];

/** Each column set, in print order. A set names columns and nothing about where they sit. */
export const PRINT_COLUMN_SET_COLUMNS: Readonly<Record<PrintColumnSet, readonly PrintLineColumn[]>> = {
  Transactional: TRANSACTIONAL_COLUMNS,
  // The optional Image column, drawn after the description so a photo sits beside what it shows.
  TransactionalWithImage: ['lineNumber', 'item', 'description', 'image', 'mfrPart', 'qty', 'unitPrice', 'discount', 'amount'],
  // 🔒 T117 (decision 0641): the manufacturer's reference name as its own column beside the part number, off by default and chosen here.
  TransactionalWithReference: ['lineNumber', 'item', 'description', 'mfrPart', 'mfrReference', 'qty', 'unitPrice', 'discount', 'amount'],
  // 🔨 A packing slip carries no price of any kind, which is why it is a column set rather than a zeroed total.
  Packing: ['lineNumber', 'item', 'description', 'mfrPart', 'qty'],
  // The Field family: the asset a line is against, the hours behind it, the rate and the level that was sent.
  Field: ['lineNumber', 'asset', 'description', 'hours', 'level', 'rate', 'amount'],
  // Decision 0515: a payment receipt lists the invoices it settled rather than lines of its own.
  Receipt: ['lineNumber', 'invoice', 'description', 'settled'],
  // The voucher stub repeats what the check band says, narrowed to what a payee files.
  Stub: ['lineNumber', 'invoice', 'description', 'settled'],
  /**
   * 🔨 Decision 0223: the customer statement is a Transactional template with
   * its own Region 8, the running-balance ledger across many documents. The
   * charge and the credit stand in separate columns, because a customer reading
   * one column of signed figures cannot tell which a figure is.
   */
  Statement: ['lineNumber', 'entryDate', 'document', 'charge', 'credit', 'balance'],
  /**
   * 🔨 Decision 0613: a request for quotation carries the Unit Price column with
   * nothing in it, because the price is what the vendor is being asked for. It
   * carries no Discount and no Amount: there is nothing yet to discount and
   * nothing to extend.
   */
  Request: ['lineNumber', 'item', 'description', 'mfrPart', 'qty', 'unitPrice'],
  /**
   * 🔒 Decision 0862: a bill of materials is a costed list, so it carries the
   * unit cost and the extended cost under headings that say cost, and no
   * discount and no manufacturer's part number.
   */
  Materials: ['lineNumber', 'item', 'description', 'qty', 'unitCost', 'extendedCost'],
  /**
   * 🔨 Decision 2911: the Service Report's lines worked carry the hours, the
   * level and the work performed, and no rate and no amount: the report prints
   * no price, and the invoice from the job is where a figure is billed.
   */
  Service: ['lineNumber', 'description', 'hours', 'level', 'workPerformed'],
};

export function printColumnSetColumns(set: PrintColumnSet | null | undefined, family: PrintFamily): readonly PrintLineColumn[] {
  if (set && set in PRINT_COLUMN_SET_COLUMNS) return PRINT_COLUMN_SET_COLUMNS[set];
  return PRINT_COLUMN_SET_COLUMNS[family === 'Field' ? 'Field' : family === 'Negotiable' ? 'Stub' : 'Transactional'];
}

/**
 * 🔒 What each column set prints, as the Column set explanation reads it (T194;
 * decision 0657): the set's own name and the headings the sheet draws, in order,
 * the merged Item and Description as the one heading it prints under. It is read
 * off the sets themselves, so the explanation cannot list a column the sheet
 * does not draw.
 */
export function printColumnSetExplained(): readonly (readonly [string, string])[] {
  return PRINT_COLUMN_SETS.map((set) => {
    const columns = PRINT_COLUMN_SET_COLUMNS[set];
    return [printColumnSetLabel(set), printDrawnColumns(columns).map((column) => printColumnHeading(column, columns)).join(', ')] as const;
  });
}

/** The regions each family numbers above ten, by number, so a caller names Region 11 rather than guessing. */
export const FIELD_EXTRA_REGIONS: Readonly<Record<number, string>> = {
  11: 'Scheduled Date',
  12: 'Technician',
  13: 'Personal Protective Equipment',
  14: 'Completion Signature',
  // 🔒 Decision 2911: the Service Report's own seven, seeded on its template alone and never on a Work Order's.
  15: 'The Trip',
  16: 'Problem Reported',
  17: 'Parts Fitted',
  18: 'Checklist Results',
  19: 'Notes',
  20: 'Customer Comment',
  21: 'Customer Signature',
};

/** 🔒 The regions the Service Report numbers above the Field family's four (decision 2911). */
export const SERVICE_REPORT_REGION_NUMBERS: readonly number[] = [15, 16, 17, 18, 19, 20, 21];

/**
 * 🔒 The check's own regions above ten (decision 0558). Each one draws a single
 * value with no caption, because pre-printed stock already carries "Date", "Pay
 * to the order of", the dollar box and "Memo". There is no region 11: the bank's
 * MICR line is on the stock and this application never draws one.
 */
export const NEGOTIABLE_EXTRA_REGIONS: Readonly<Record<number, string>> = {
  12: 'Amount in Words',
  13: 'Memo',
  14: 'Pay To',
  15: 'Date',
  16: 'Amount',
};

/** The six things a check prints, minus the payee's address block, which Region 5 draws. */
export interface CheckRegionValues {
  words: string;
  memo: string;
  payTo: string;
  date: string;
  amount: string;
}

/**
 * 🔒 The check's five value regions, numbered and labelled in one place
 * (decision 0558), so the print projection, the editor's sample and the
 * alignment test print cannot come to disagree about which region draws what.
 * The label is what the region editor shows; the sheet prints the value alone.
 */
export function checkRegionValues(values: CheckRegionValues): PrintRegionValue[] {
  return [
    { regionNumber: 12, label: t('document.print.region.amountInWords'), value: values.words },
    { regionNumber: 13, label: t('document.print.region.memo'), value: values.memo },
    { regionNumber: 14, label: t('document.print.region.payTo'), value: values.payTo },
    { regionNumber: 15, label: t('document.print.region.checkDate'), value: values.date },
    { regionNumber: 16, label: t('document.print.region.numericAmount'), value: values.amount },
  ];
}

/** The name Region N carries in a family; the ten are shared and the rest are the family's own. */
export function printRegionNameFor(family: PrintFamily, regionNumber: number): string {
  if (regionNumber >= 1 && regionNumber <= PRINT_REGIONS.length) return PRINT_REGIONS[regionNumber - 1]!;
  const extra = family === 'Field' ? FIELD_EXTRA_REGIONS[regionNumber] : family === 'Negotiable' ? NEGOTIABLE_EXTRA_REGIONS[regionNumber] : undefined;
  if (!extra) throw new Error(`region ${regionNumber} belongs to no ${family} template (piece 27)`);
  return extra;
}

/**
 * 🔒 The eight sections the template editor's rail offers (decisions 0615,
 * 0697, 1533), in the order it draws them, each naming the regions whose fields
 * it holds. The rail is Zoho's and the region model is this plan's, so the mapping
 * lives here rather than in the screen: the piece, the editor and the cases that
 * drive it read one list.
 *
 * 🔒 The three addresses stand in one section, Addresses (T204; decision 0697):
 * the return address, Bill To and Deliver To. The owner, 2026-09-24: *"make all
 * address locations one tab."* They are the regions a window envelope is laid
 * out around, so the envelope band's hint stands with them.
 *
 * 🔒 Positions, second on the rail, holds every region's X, Y, Max width, Max
 * height and Enabled in one table (T796; decision 1533). The owner, 2026-10-02:
 * *"also change this that all positions are on one tab"*. It claims no region,
 * because it holds every region's geometry and none of a region's own fields.
 *
 * 🔨 The ten regions partition across the other seven with nothing left over,
 * and a region a family numbers above ten falls to Other Details. There are no
 * footer regions among the ten, so Header & Footer holds Region 3 alone.
 */
export const PRINT_EDITOR_SECTIONS = [
  { key: 'general', labelKey: 'print.templates.sectionGeneral', regions: [1] },
  { key: 'positions', labelKey: 'print.templates.sectionPositions', regions: [] },
  { key: 'addresses', labelKey: 'print.templates.sectionAddresses', regions: [2, 5, 6] },
  { key: 'header', labelKey: 'print.templates.sectionHeaderFooter', regions: [3] },
  { key: 'transaction', labelKey: 'print.templates.sectionTransaction', regions: [4] },
  { key: 'table', labelKey: 'print.templates.sectionTable', regions: [8] },
  { key: 'total', labelKey: 'print.templates.sectionTotal', regions: [9] },
  { key: 'other', labelKey: 'print.templates.sectionOther', regions: [7, 10] },
] as const satisfies readonly { key: string; labelKey: StringKey; regions: readonly number[] }[];

export type PrintEditorSectionKey = (typeof PRINT_EDITOR_SECTIONS)[number]['key'];

/** 🔨 The page size, the four margins and Region 1's own fields stand in this section and no other. */
export const PRINT_GENERAL_SECTION: PrintEditorSectionKey = 'general';

/** 🔒 Every region's geometry and Enabled tick in one table, and nothing else (decision 1533). */
export const PRINT_POSITIONS_SECTION: PrintEditorSectionKey = 'positions';

/** 🔒 The section the envelope band's hint stands in, with the three addresses (decision 0697). */
export const PRINT_ADDRESSES_SECTION: PrintEditorSectionKey = 'addresses';

/** Where a region a family numbers above ten falls, because nothing narrower claims it. */
export const PRINT_OTHER_SECTION: PrintEditorSectionKey = 'other';

/** The section whose properties column holds this region's fields; anything above ten is Other Details. */
export function printSectionForRegion(regionNumber: number): PrintEditorSectionKey {
  const named = PRINT_EDITOR_SECTIONS.find((section) => (section.regions as readonly number[]).includes(regionNumber));
  return named ? named.key : PRINT_OTHER_SECTION;
}

/**
 * 🔒 Whether the preview boxes a region in the open section's blue (decisions
 * 0696, 1533): every region on Positions, which holds every region's geometry,
 * and the section's own regions on any other. A region turned off stays grey
 * whatever is open; the renderer reads that flag beside this.
 */
export function printSectionBoxesRegion(section: PrintEditorSectionKey, regionNumber: number): boolean {
  return section === PRINT_POSITIONS_SECTION || printSectionForRegion(regionNumber) === section;
}

/** One seeded region: where it sits on the page the first time the alignment print runs. */
export interface PrintRegionDefault {
  regionNumber: number;
  regionName: string;
  xInches: number;
  yInches: number;
  maxWidthInches: number;
  maxHeightInches: number;
  isEnabled: boolean;
  bandRole: PrintBandRole;
  columnSet: PrintColumnSet | null;
}

function region(family: PrintFamily, regionNumber: number, x: number, y: number, w: number, h: number, extra: { isEnabled?: boolean; bandRole?: PrintBandRole; columnSet?: PrintColumnSet | null } = {}): PrintRegionDefault {
  return {
    regionNumber,
    regionName: printRegionNameFor(family, regionNumber),
    xInches: x,
    yInches: y,
    maxWidthInches: w,
    maxHeightInches: h,
    isEnabled: extra.isEnabled ?? true,
    bandRole: extra.bandRole ?? 'Page',
    columnSet: extra.columnSet ?? null,
  };
}

/**
 * 🔒 The Transactional family's ten are the product owner's own layout of
 * 2026-10-02 (decision 1532, amending 0575 and 0660 for this family): *"default
 * document print settings"*. The mark and the title share the top line, the
 * issuing address stands under the mark, the facts table under the title, and
 * Deliver To on the line the recipient's address would hold.
 *
 * 🔒 Region 5, the counterparty's address, ships turned on inside the address
 * window (decision 1933, amending 1532 on the owner's *"default region 5 bill
 * too on"*): the customer on a sales document, the vendor on a purchase order.
 * Region 9 was the one region the owner's screenshots did not show; it kept
 * its place until the owner set its top at 9.0 (decision 1624), level with the
 * terms and flush under the line table's seeded foot.
 *
 * 💰 Region 2 stands below the return window of a number ten double-window
 * envelope and the mark stands in it, so a sheet folded into one shows the logo
 * through the upper cutout and no address through either. The one window
 * refusal still holds Region 2, which is all it holds.
 *
 * 🔨 Region 9's right edge is Region 8's right edge, which is what puts the
 * Total under the Amount column rather than beside it.
 *
 * 🔨 Every seeded region sits inside the half-inch margin the four `print.margin*`
 * rows ship at, so nothing on a default sheet is drawn where an office printer
 * may not reach.
 */
export const TRANSACTIONAL_REGION_DEFAULTS: readonly PrintRegionDefault[] = [
  region('Transactional', 1, 0.5, 0.5, 4.3, 0.65),
  region('Transactional', 2, 0.55, 1.5, 3.15, 0.75),
  region('Transactional', 3, 5.0, 0.5, 3.0, 0.65),
  region('Transactional', 4, 5.0, 1.17, 3.0, 1.1),
  region('Transactional', 5, 0.95, 2.25, 3.8, 1.0),
  region('Transactional', 6, 5.0, 2.25, 3.0, 1.0),
  region('Transactional', 7, 0.5, 3.42, 7.5, 0.53),
  region('Transactional', 8, 0.5, 4.0, 7.5, 5.0, { columnSet: 'Transactional' }),
  region('Transactional', 9, 4.6, 9.0, 3.4, 1.5),
  region('Transactional', 10, 0.5, 9.0, 4.0, 1.5),
];

/**
 * The Field family: the ten in the places the Transactional family held until
 * 2026-10-02 (decisions 0575, 0660), with the job's own four
 * across the sheet above the line table, where a technician reads them before
 * the work rather than after it. Region 9 ships enabled and draws nothing where
 * the projection carries no totals, which is how a job with no figures prints
 * without one.
 */
export const FIELD_REGION_DEFAULTS: readonly PrintRegionDefault[] = [
  region('Field', 1, 0.5, 1.42, 4.3, 0.78),
  region('Field', 2, 0.55, 0.52, 3.15, 0.85),
  region('Field', 3, 5.0, 0.5, 3.0, 0.65),
  region('Field', 4, 5.0, 1.17, 3.0, 1.2),
  region('Field', 5, 0.95, 2.4, 3.8, 0.95),
  region('Field', 6, 5.0, 2.4, 3.0, 0.95),
  region('Field', 7, 0.5, 3.42, 7.5, 0.53),
  region('Field', 8, 0.5, 4.6, 7.5, 3.5, { columnSet: 'Field' }),
  region('Field', 9, 4.6, 8.7, 3.4, 1.2),
  region('Field', 10, 0.5, 8.7, 4.0, 1.8),
  region('Field', 11, 0.5, 4.0, 2.0, 0.45),
  region('Field', 12, 2.6, 4.0, 2.4, 0.45),
  region('Field', 13, 5.1, 4.0, 2.9, 0.45),
  region('Field', 14, 0.5, 8.2, 4.0, 0.4),
];

/**
 * 🔒 One voucher stub's regions, seeded identically on both stubs (decision
 * 0558): the date and the payee across the top, the bills the check settled
 * through the stub column set, the total right-aligned at the foot, and the memo
 * beside it. Each stub repeats the check's own data, which is what a payee files.
 */
function stubRegions(bandRole: PrintBandRole): PrintRegionDefault[] {
  return [
    region('Negotiable', 15, 0.69, 0.35, 1.0, 0.25, { bandRole }),
    region('Negotiable', 14, 1.72, 0.35, 4.0, 0.25, { bandRole }),
    region('Negotiable', 8, 0.69, 0.8, 7.11, 2.0, { bandRole, columnSet: 'Stub' }),
    region('Negotiable', 9, 5.8, 2.9, 2.0, 0.3, { bandRole }),
    region('Negotiable', 13, 0.69, 3.15, 4.5, 0.3, { bandRole }),
  ];
}

/**
 * 🔒 Region 13's place on the check band (decision 7630), and the place it held
 * before, which the migration and Reset to defaults read: a template still at the
 * old place moves, and one somebody positioned keeps theirs.
 */
export const CHECK_MEMO_SEED = { xInches: 4.85, yInches: 2.12, widthInches: 2.95, heightInches: 0.25 } as const;
export const CHECK_MEMO_SEED_BEFORE = { xInches: 0.69, yInches: 3.0, widthInches: 4.5, heightInches: 0.3 } as const;

/**
 * 🔒 The Negotiable family on laser voucher check stock (decision 0558): the
 * check across the top three and a half inches, then two voucher stubs at the
 * perforations. The check band's numbers are the published Quicken and
 * QuickBooks US Letter check format, converted from points to inches, so a
 * template printed on that stock lands in the pre-printed boxes.
 *
 * 🔨 Regions 1, 2 and 3 ship disabled on the check band. The logo, the return
 * address and the title are printed on the stock, and drawing a second copy over
 * the first is what wastes a sheet. They keep their geometry, so a person who
 * wants one turns it on rather than positioning it from nothing.
 *
 * 🔨 Region 5's Y is the one number that departs from the Quicken format. That
 * format puts the address block's first line at 2.72 inches, which is the bottom
 * edge of the payee window of a number eight double-window envelope, so a block
 * of four lines runs out under the cutout and shows one of them. Seeded at 2.1
 * across 0.7 inches the whole block sits inside the window, and it clears the
 * amount in words above it: 💰 seeded at 1.95 the two blocks overlapped by a
 * tenth of an inch and the geometry rule, which measures a region against its
 * band and never against its neighbours, said nothing (found in review,
 * 2026-09-18).
 */
export const NEGOTIABLE_REGION_DEFAULTS: readonly PrintRegionDefault[] = [
  region('Negotiable', 1, 0.5, 0.4, 2.5, 0.6, { bandRole: 'CheckBand', isEnabled: false }),
  region('Negotiable', 2, 0.5, 1.05, 3.0, 0.9, { bandRole: 'CheckBand', isEnabled: false }),
  region('Negotiable', 3, 6.0, 0.4, 2.0, 0.4, { bandRole: 'CheckBand', isEnabled: false }),
  region('Negotiable', 5, 1.25, 2.1, 3.1, 0.7, { bandRole: 'CheckBand' }),
  region('Negotiable', 12, 1.25, 1.82, 6.5, 0.25, { bandRole: 'CheckBand' }),
  /*
   * 🔒 The memo stands on the right above the signature line, clear of the MICR band (decision 7630). The published
   * format put it at 0.69 and 3.0, inside the band that starts at 2.875, where the bank's reader expects blank paper;
   * the left beneath the words is the payee's address in the envelope's window.
   */
  region('Negotiable', 13, CHECK_MEMO_SEED.xInches, CHECK_MEMO_SEED.yInches, CHECK_MEMO_SEED.widthInches, CHECK_MEMO_SEED.heightInches, { bandRole: 'CheckBand' }),
  region('Negotiable', 14, 1.25, 1.47, 5.56, 0.25, { bandRole: 'CheckBand' }),
  region('Negotiable', 15, 6.94, 0.99, 1.3, 0.25, { bandRole: 'CheckBand' }),
  // 🔨 The figure alone: the box and the dollar sign are pre-printed on the stock.
  region('Negotiable', 16, 6.94, 1.47, 1.2, 0.25, { bandRole: 'CheckBand' }),
  ...stubRegions('StubBand'),
  ...stubRegions('SecondStubBand'),
];

export function printRegionDefaults(family: PrintFamily): readonly PrintRegionDefault[] {
  return family === 'Field' ? FIELD_REGION_DEFAULTS : family === 'Negotiable' ? NEGOTIABLE_REGION_DEFAULTS : TRANSACTIONAL_REGION_DEFAULTS;
}

/**
 * 🔒 The Service Report's own template (decision 2911): the Field family's
 * letterhead, addresses and job facts where the Work Order holds them, the
 * trip and the problem reported beside each other above the lines worked,
 * the parts and the checklist results beneath them, the notes across the
 * sheet, then the customer's comment and the closing line, and the two
 * signatures at the foot, the customer's on the left and the lead's on the
 * right. Region 9 ships off: the report prints no price (5a).
 *
 * 🔨 Region 7 carries the work done in the lead's words and Region 6 the job's
 * site, as the frozen sheet printed them before the template was its own.
 */
export const SERVICE_REPORT_REGION_DEFAULTS: readonly PrintRegionDefault[] = [
  region('Field', 1, 0.5, 1.42, 4.3, 0.78),
  region('Field', 2, 0.55, 0.52, 3.15, 0.85),
  region('Field', 3, 5.0, 0.5, 3.0, 0.65),
  region('Field', 4, 5.0, 1.17, 3.0, 1.2),
  region('Field', 5, 0.95, 2.4, 3.8, 0.95),
  region('Field', 6, 5.0, 2.4, 3.0, 0.95),
  region('Field', 7, 0.5, 3.42, 7.5, 0.53),
  region('Field', 8, 0.5, 5.1, 7.5, 1.5, { columnSet: 'Service' }),
  region('Field', 9, 4.6, 8.7, 3.4, 1.2, { isEnabled: false }),
  region('Field', 10, 4.3, 8.95, 3.7, 0.45),
  region('Field', 11, 0.5, 4.0, 2.0, 0.45),
  region('Field', 12, 2.6, 4.0, 2.4, 0.45),
  region('Field', 13, 5.1, 4.0, 2.9, 0.45),
  region('Field', 14, 4.3, 9.45, 3.7, 1.05),
  region('Field', 15, 0.5, 4.5, 3.7, 0.55),
  region('Field', 16, 4.3, 4.5, 3.7, 0.55),
  region('Field', 17, 0.5, 6.65, 3.7, 1.0),
  region('Field', 18, 4.3, 6.65, 3.7, 1.0),
  region('Field', 19, 0.5, 7.7, 7.5, 1.2),
  region('Field', 20, 0.5, 8.95, 3.7, 0.45),
  region('Field', 21, 0.5, 9.45, 3.7, 1.05),
];

/**
 * 🔒 The regions a template of this document type is seeded with (decision
 * 2911): the Service Report's own, and every other type its family's.
 */
export function printRegionDefaultsFor(documentType: string): readonly PrintRegionDefault[] {
  return documentType === 'ServiceReport' ? SERVICE_REPORT_REGION_DEFAULTS : printRegionDefaults(printFamilyOf(documentType));
}

/** A rectangle a mailing address is measured against, in inches inside its own band. */
export interface PrintWindow {
  minXInches: number;
  maxXInches: number;
  minYInches: number;
  maxYInches: number;
}

/**
 * 🔒 The return window of a standard number ten double-window envelope (T189;
 * decisions 0557, 0660), in page inches for a US Letter sheet folded in thirds
 * with the top panel outward: half an inch from the left and half an inch from
 * the top, 3.25 inches wide and seven-eighths of an inch deep. The Field family's
 * Region 2 is seeded inside it, so the issuing address shows through the upper
 * cutout; the Transactional family's is the owner's own place (decision 1532).
 */
export const RETURN_ADDRESS_WINDOW: Readonly<PrintWindow> = { minXInches: 0.5, maxXInches: 3.75, minYInches: 0.5, maxYInches: 1.375 };

/**
 * 🔒 The address window of the same envelope (T189; decisions 0557, 0660):
 * seven-eighths of an inch from the left and two and a quarter inches from the
 * top, four inches wide and an inch and an eighth deep. Region 5's default is
 * seeded inside it, so the customer's or the vendor's address shows through the
 * lower cutout rather than below the fold, where nothing shows.
 */
export const RECIPIENT_ADDRESS_WINDOW: Readonly<PrintWindow> = { minXInches: 0.875, maxXInches: 4.875, minYInches: 2.25, maxYInches: 3.375 };

/** The window each of the two mailing regions is seeded inside, on the Field family (decision 1532); the refusal below is wider than either. */
export const ENVELOPE_DEFAULT_WINDOWS: Readonly<Record<number, Readonly<PrintWindow>>> = {
  2: RETURN_ADDRESS_WINDOW,
  5: RECIPIENT_ADDRESS_WINDOW,
};

/**
 * 🔒 The payee window of a number eight double-window envelope, measured inside
 * the check's own band (decision 0558). A check mails unfolded, so the envelope
 * is the small one and its lower cutout sits seven-eighths of an inch from the
 * left, three and a half inches wide, three-quarters of an inch up from the foot
 * and seven-eighths tall — which on a three-and-a-half-inch check is this.
 */
export const CHECK_RECIPIENT_WINDOW: Readonly<PrintWindow> = { minXInches: 0.9, maxXInches: 4.4, minYInches: 1.9, maxYInches: 2.8 };

/**
 * The window one region's default is seeded inside, measured in its own band.
 * 🔨 A window places a default and refuses nothing (decision 0557); the one
 * refusal is `ENVELOPE_WINDOW` below, and it is wider than any of these.
 */
export function printDefaultWindowFor(regionNumber: number, bandRole: PrintBandRole = 'Page'): Readonly<PrintWindow> | null {
  if (bandRole === 'CheckBand') return regionNumber === 5 ? CHECK_RECIPIENT_WINDOW : null;
  if (bandRole !== 'Page') return null;
  return ENVELOPE_DEFAULT_WINDOWS[regionNumber] ?? null;
}

/**
 * 🔒 The one refusal on a mailing address (decision 0557, amending 0517): the
 * top third of the page, between 0.5 inches and the recipient window's own right
 * edge across, and between 0.4 inches and the fold down, measured inside the
 * region's own band.
 *
 * 🔨 The floor is `LETTER_TOP_THIRD_INCHES` and never a literal. It was 4.0
 * inches until 2026-09-18, which let an address block end a third of an inch
 * past the crease of a sheet folded in thirds — where a window envelope shows
 * nothing at all — and the specification puts both addresses in the top third.
 *
 * 🔨 The refusal is the band and not the two windows. A person nudges an address
 * anywhere the envelope can expose it; the windows say only where the DEFAULTS
 * are seeded, which is what the specification asks of them.
 */
export function printEnvelopeWindow(page: { widthInches: number; heightInches: number } = LETTER_PAGE): Readonly<PrintWindow> {
  return {
    minXInches: 0.5,
    maxXInches: RECIPIENT_ADDRESS_WINDOW.maxXInches,
    minYInches: 0.4,
    maxYInches: printTopThirdInches(page),
  };
}

/** The window on a US Letter sheet, which is what the seeds are placed inside. */
export const ENVELOPE_WINDOW: Readonly<PrintWindow> = printEnvelopeWindow(LETTER_PAGE);
export const ENVELOPE_WINDOW_REGIONS: readonly number[] = [2, 5];

/** Whether a region's whole block sits inside a window, the way an envelope shows all of an address or none of it. */
export function insidePrintWindow(geometry: { xInches: number; yInches: number; maxWidthInches: number; maxHeightInches: number }, window: Readonly<PrintWindow>): boolean {
  return (
    geometry.xInches >= window.minXInches - 0.001 &&
    geometry.xInches + geometry.maxWidthInches <= window.maxXInches + 0.001 &&
    geometry.yInches >= window.minYInches - 0.001 &&
    geometry.yInches + geometry.maxHeightInches <= window.maxYInches + 0.001
  );
}

/** The geometry a region row may carry; a number outside the page is not a layout, it is a lost region. */
export interface PrintRegionGeometry {
  regionNumber: number;
  xInches: number;
  yInches: number;
  maxWidthInches: number;
  maxHeightInches: number;
  /** The band the measurements are inside; the page where a row names none. */
  bandRole?: PrintBandRole;
}

/**
 * The one sentence a refused region carries, or null when the geometry holds.
 * 🔨 Every geometry rule lives here, so the editor, the API and the renderer
 * cannot come to disagree about what fits on a page.
 */
export function printRegionProblem(geometry: PrintRegionGeometry, page: { widthInches: number; heightInches: number } = LETTER_PAGE): string | null {
  const numbers = [geometry.xInches, geometry.yInches, geometry.maxWidthInches, geometry.maxHeightInches];
  if (numbers.some((value) => !Number.isFinite(value))) return 'a region carries four measurements in inches';
  if (geometry.xInches < 0 || geometry.yInches < 0) return 'a region sits on the page, so neither measurement is negative';
  if (geometry.maxWidthInches <= 0 || geometry.maxHeightInches <= 0) return 'a region with no width or no height draws nothing; turn it off instead';
  const area = printBandArea(geometry.bandRole ?? 'Page', page);
  if (geometry.xInches + geometry.maxWidthInches > area.widthInches + 0.001) return `a region ends inside the ${area.widthInches} inch page, and this one runs off the right edge`;
  if (geometry.yInches + geometry.maxHeightInches > area.heightInches + 0.001) return `a region ends inside the ${area.heightInches} inch band, and this one runs off the bottom edge`;
  const envelope = printEnvelopeWindow(page);
  if (ENVELOPE_WINDOW_REGIONS.includes(geometry.regionNumber) && !insidePrintWindow(geometry, envelope)) {
    return `region ${geometry.regionNumber} is a mailing address and stays inside the envelope window, between ${inchesLabel(envelope.minXInches)} and ${inchesLabel(envelope.maxXInches)} inches across and ${inchesLabel(envelope.minYInches)} and ${inchesLabel(envelope.maxYInches)} inches down, where the fold falls on a sheet folded in thirds`;
  }
  return null;
}

/**
 * One row of the set the editor saves: the geometry it may change, the flags
 * beside it, and the row's own identity. 🔨 A region's number and its band are
 * the template's and never a person's, so neither is written from a draft.
 */
export interface PrintRegionSetRow extends PrintRegionGeometry {
  id: string;
  isEnabled: boolean;
  labelOverride: string | null;
  columnSet: string | null;
}

/** A refused layout: the row that caused it, by id and by number, and the one sentence a person reads. */
export interface PrintRegionSetRefusal {
  /** Empty where the refusal is the layout's and no single row caused it. */
  id: string;
  regionNumber: number;
  message: string;
}

/**
 * 🔒 The refusal a whole region set carries, or null where every row holds
 * (decision 0567). The editor sends one layout and the write is one
 * transaction, so the set is judged as a set: the row the one geometry rule
 * refuses names itself in the sentence, because a person reading "runs off the
 * right edge" over ten rows cannot tell which row to fix.
 *
 * 🔨 It answers the row's own id beside the sentence, so a screen marks the row
 * that caused it rather than matching the number out of the words — a match on
 * the text alone marked Region 1 for a refusal naming Region 10 (found in
 * review, 2026-09-19).
 */
export function printRegionSetRefusal(rows: readonly PrintRegionSetRow[], page: { widthInches: number; heightInches: number } = LETTER_PAGE): PrintRegionSetRefusal | null {
  if (rows.length === 0) return { id: '', regionNumber: 0, message: 'a layout carries its regions, and this one carries none' };
  const seen = new Set<string>();
  for (const row of rows) {
    const named = (message: string): PrintRegionSetRefusal => ({ id: row.id, regionNumber: row.regionNumber, message });
    if (seen.has(row.id)) return named(`region ${row.regionNumber} is sent twice in one layout`);
    seen.add(row.id);
    if (row.columnSet !== null && !(PRINT_COLUMN_SETS as readonly string[]).includes(row.columnSet)) {
      return named(`region ${row.regionNumber}: a column set is one of ${PRINT_COLUMN_SETS.join(', ')}`);
    }
    const problem = printRegionProblem(row, page);
    if (problem) return named(`region ${row.regionNumber}: ${problem}`);
  }
  return null;
}

/**
 * The page one template is laid out on, as the editor sends it: the size and the
 * four margins, in inches.
 */
export interface PrintPageSetting {
  widthInches: number;
  heightInches: number;
  marginTopInches: number;
  marginLeftInches: number;
  marginRightInches: number;
  marginBottomInches: number;
}

/** Which margin a refusal is about, in the order the page reads them. */
const PAGE_MARGINS: readonly (readonly [string, keyof PrintPageSetting])[] = [
  ['top', 'marginTopInches'],
  ['left', 'marginLeftInches'],
  ['right', 'marginRightInches'],
  ['bottom', 'marginBottomInches'],
];

/**
 * 🔒 The refusal a page carries, or null where it holds (decision 0577): a size
 * that is neither of the two, or a margin outside nought to two inches. The
 * margin rule is the one the Print defaults page already refuses by, so the
 * seeded figure and the template's own figure are judged alike.
 *
 * 🔨 It names the margin. Four fields refused by one sentence is a sentence
 * nobody can act on.
 */
export function printPageRefusal(page: PrintPageSetting, family?: PrintFamily): string | null {
  const size = printPageSizeOf(page);
  if (size === null) return `a template is laid out on ${PRINT_PAGE_SIZES.join(' or ')}, portrait`;
  /**
   * 🔒 A check is US Letter, whoever asks (decision 0577). The stock is bought
   * perforated at three and a half and seven inches down a Letter sheet, so an
   * A4 check is a sheet nobody sells. 🔨 The rule is here and not only on the
   * screen that hides the control: a screen that hides a choice is not a rule,
   * and a request built by hand met nothing at all (found in review,
   * 2026-09-19).
   */
  if (family === 'Negotiable' && size !== 'Letter') {
    return `a ${family} template is laid out on Letter: check stock is pre-printed and perforated at ${PRINT_BAND_ORIGIN_INCHES.StubBand} and ${PRINT_BAND_ORIGIN_INCHES.SecondStubBand} inches down a Letter sheet`;
  }
  for (const [name, key] of PAGE_MARGINS) {
    const problem = printMarginProblem(page[key]);
    if (problem) return `the ${name} margin: ${problem}`;
  }
  return null;
}

/** The refusal as the one sentence a person reads, for a caller with no row to mark. */
export function printRegionSetProblem(rows: readonly PrintRegionSetRow[], page: { widthInches: number; heightInches: number } = LETTER_PAGE): string | null {
  return printRegionSetRefusal(rows, page)?.message ?? null;
}

/** The answer shape the editor reads for one region row; declared once so the browser and the server agree. */
export interface PrintTemplateRegionRow {
  id: string;
  regionNumber: number;
  regionName: string;
  xInches: number;
  yInches: number;
  maxWidthInches: number;
  maxHeightInches: number;
  isEnabled: boolean;
  labelOverride: string | null;
  columnSet: string | null;
  bandRole: PrintBandRole;
}

/**
 * The answer shape the gallery reads for one template. 🔨 It is the summary and
 * not a `PrintTemplateRow`: that name is the table of a template's content rows
 * (decision 0656), and one name for two shapes is a type nobody can read.
 */
export interface PrintTemplateSummary {
  id: string;
  family: PrintFamily;
  documentType: string;
  name: string;
  isDefault: boolean;
  companyId: string | null;
  companyName: string | null;
  pageWidthInches: number;
  pageHeightInches: number;
  marginTopInches: number;
  marginLeftInches: number;
  marginRightInches: number;
  marginBottomInches: number;
  isActive: boolean;
}

export interface PrintTemplateDetail extends PrintTemplateSummary {
  regions: readonly PrintTemplateRegionRow[];
  /** 🔒 The typeface, the sizes, the title's colour and the page number (decision 0656). */
  style: PrintTemplateStyle;
  /** 🔒 The content rows this template changes from the default; a row it does not carry is the default (decision 0656). */
  rows: readonly PrintTemplateContentRow[];
  /** Where the live preview pane points; the same HTML the sheet is made from. */
  previewPath: string;
}

/** The editor's list, grouped the way it is drawn: by family, then by document type. */
export interface PrintTemplateListAnswer {
  rows: readonly PrintTemplateSummary[];
  families: readonly PrintFamily[];
}

/** The page a sheet is drawn on, in inches: the paper and the four margins it keeps. */
export interface PrintSheetPage {
  widthInches: number;
  heightInches: number;
  marginTopInches: number;
  marginLeftInches: number;
  marginRightInches: number;
  marginBottomInches: number;
}

/**
 * 🔒 The resolved layout a document printed on, as an answer carries it
 * (decision 0567): the template that won the resolution, its page, and its
 * region rows. The document page's view mode positions these rows, so a region
 * nudged in the editor moves on the page as it moves on the sheet.
 *
 * 🔨 It is the same shape the server renderer takes, so the screen and the
 * sheet read one layout rather than two readings of one template.
 */
/**
 * 🔒 The sheet a document page draws, as the renderer's own HTML (T169). The
 * product owner, 2026-09-22: *"PDF View does not match the actual pdf."* and,
 * asked how it should behave, *"i dont want a wait like preview PDF has. I want
 * instant"*.
 *
 * 🔒 It travels in the page's own answer, so the sheet is on screen in the same
 * paint as the page and there is no second read and no spinner. It is the same
 * markup and the same stylesheet the printer prints, so the download is the same
 * picture.
 *
 * 🔨 The status ribbon is the one thing the screen adds (decision 0612). It is a
 * screen affordance and is not in this HTML, so it cannot reach the paper.
 */
export interface PrintSheetHtml {
  /** The whole document, one `sheet` element per printed page, with its stylesheet inline. */
  html: string;
  /** How many pages the printer would produce, which is where the pane draws its break rules. */
  pageCount: number;
  /**
   * 🔒 Present where a stored region falls outside the one geometry rule: one
   * line for each such region, naming the template and the region, which the
   * page draws as one warning above the sheet, opening once and giving the fix
   * once (decision 1401, amending 1340; T591). The sheet is still drawn, each
   * region where it is stored.
   */
  warnings?: readonly string[];
}

export interface PrintSheetLayout {
  id: string;
  family: PrintFamily;
  documentType: string;
  name: string;
  page: PrintSheetPage;
  regions: readonly PrintTemplateRegionRow[];
  /** 🔒 The typeface, the sizes, the title's colour and the page number; absent draws the defaults (decision 0656). */
  style?: PrintTemplateStyle;
  /** 🔒 The content rows the template changes; absent or empty draws every default (decision 0656). */
  rows?: readonly PrintTemplateContentRow[];
  /**
   * 🔒 The organisation's one logo, which Region 1 draws (decision 0575).
   * The screen reads it through the file's ordinary read route; the sheet takes
   * the same bytes inline, because its renderer reaches no network.
   */
  logoFileId?: string | null;
}
